import { useEffect, useRef, useState } from "react";
import { Icon } from "../../../components/ui/Icon";
import { useToast } from "../../../components/feedback/Toast";
import { useClickOutside } from "../../../lib/useClickOutside";
import type { Branding } from "../../../branding/branding.config";
import {
  previewBranding,
  resetBranding,
  loadPersistedBranding,
  persistBranding,
  shippedBranding,
  useBranding,
} from "../../../branding/brandingStore";
import { readLogoColors } from "../../../branding/logoColors";

const HEX = /^#[0-9a-f]{6}$/i;

/** Normalises what a person types: `1b3e6f`, `#1B3E6F` and `#1b3` all pass. */
function normalizeHex(value: string): string | null {
  const raw = value.trim().replace(/^#/, "");
  const full = raw.length === 3 ? raw.split("").map((c) => c + c).join("") : raw;
  const candidate = `#${full}`;
  return HEX.test(candidate) ? candidate.toLowerCase() : null;
}

interface Props {
  /** `settings.update` — without it the screen reads but does not write. */
  canManage: boolean;
}

/**
 * Branding & colors — the deployment identity, live.
 *
 * The canvas draws one bordered card of labelled rows: the two brand colours
 * with a swatch, a hex field and a native picker each; the logo with its
 * "Change logo" popover (upload or paste a URL); the two colours read out of
 * that logo, offered as one click; the wordmark; and a preview row that proves
 * the palette on a filled action, an outlined one and a secondary-tinted pill —
 * the three shapes a bad brand colour breaks first.
 *
 * Everything above the footer genuinely applies, immediately, to every screen:
 * `previewBranding` writes the custom properties and re-renders the logo
 * components together. What it cannot do is OUTLIVE the tab. Saving a
 * deployment identity is a deployment-wide write, and this application's API
 * has no settings endpoint to write it to — so Save is disabled and says so,
 * rather than storing a deployment setting in one administrator's browser and
 * calling it saved.
 */
export function BrandingTab({ canManage }: Props): JSX.Element {
  const branding = useBranding();
  const shipped = shippedBranding();
  const toast = useToast();

  const [primary, setPrimary] = useState(branding.primaryColor);
  const [secondary, setSecondary] = useState(branding.secondaryColor);
  const [wordmark, setWordmark] = useState(branding.productName);
  const [logo, setLogo] = useState(branding.logoMark);
  const [logoOpen, setLogoOpen] = useState(false);
  const [logoColors, setLogoColors] = useState<string[] | null>(null);
  const [logoNote, setLogoNote] = useState<string | null>(null);
  const [saved, setSaved] = useState<Branding>(branding);
  const [saving, setSaving] = useState(false);
  const logoRef = useRef<HTMLDivElement>(null);
  useClickOutside(logoRef, logoOpen, () => setLogoOpen(false));

  const primaryHex = normalizeHex(primary);
  const secondaryHex = normalizeHex(secondary);

  useEffect(() => {
    let live = true;
    void loadPersistedBranding()
      .then((loaded) => {
        if (!live) return;
        setPrimary(loaded.primaryColor);
        setSecondary(loaded.secondaryColor);
        setWordmark(loaded.productName);
        setLogo(loaded.logoMark);
        setSaved(loaded);
      })
      .catch((error) => {
        if (live) toast.error(error instanceof Error ? error.message : "Could not load branding.");
      });
    return () => { live = false; };
  }, [toast]);

  /**
   * The edit applies as it is made — that is what "Applies live across every
   * screen" means. An incomplete hex is simply not applied; the field says so
   * and the product keeps the last good value rather than flashing black.
   */
  useEffect(() => {
    if (!primaryHex || !secondaryHex) return;
    const next: Branding = {
      ...shipped,
      primaryColor: primaryHex,
      secondaryColor: secondaryHex,
      productName: wordmark.trim() || shipped.productName,
      logoMark: logo || shipped.logoMark,
      // A custom mark has no reversed twin, so the dark chrome uses the same
      // asset rather than falling back to the deployment's own white mark and
      // showing two different logos at once.
      logoMarkLight: logo && logo !== shipped.logoMark ? logo : shipped.logoMarkLight,
    };
    previewBranding(next);
  }, [primaryHex, secondaryHex, wordmark, logo, shipped]);

  // Read the palette out of whatever logo is showing, including the one the
  // deployment shipped — the offer is useful before anything is uploaded.
  useEffect(() => {
    let live = true;
    setLogoColors(null);
    setLogoNote("Reading the logo…");
    void readLogoColors(logo).then((result) => {
      if (!live) return;
      if (result.ok) {
        setLogoColors(result.colors);
        setLogoNote(null);
      } else {
        setLogoColors(null);
        setLogoNote(result.message);
      }
    });
    return () => {
      live = false;
    };
  }, [logo]);

  function onLogoFile(file: File | null | undefined): void {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") setLogo(reader.result);
    };
    reader.onerror = () => toast.error("That file could not be read.");
    reader.readAsDataURL(file);
    setLogoOpen(false);
  }

  function swap(): void {
    setPrimary(secondary);
    setSecondary(primary);
  }

  function reset(): void {
    setPrimary(shipped.primaryColor);
    setSecondary(shipped.secondaryColor);
    setWordmark(shipped.productName);
    setLogo(shipped.logoMark);
    resetBranding();
    toast.success("Back to the deployment's own identity.");
  }

  async function save(): Promise<void> {
    if (!primaryHex || !secondaryHex) return;
    setSaving(true);
    try {
      const persisted = await persistBranding({
        ...shipped,
        primaryColor: primaryHex,
        secondaryColor: secondaryHex,
        productName: wordmark.trim() || shipped.productName,
        logoMark: logo || shipped.logoMark,
        logoMarkLight: logo && logo !== shipped.logoMark ? logo : shipped.logoMarkLight,
      });
      setSaved(persisted);
      toast.success("Deployment identity saved.");
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not save branding.");
    } finally {
      setSaving(false);
    }
  }

  const dirty =
    primaryHex !== saved.primaryColor.toLowerCase() ||
    secondaryHex !== saved.secondaryColor.toLowerCase() ||
    wordmark.trim() !== saved.productName ||
    logo !== saved.logoMark;
  const differsFromDefault =
    primaryHex !== shipped.primaryColor.toLowerCase() ||
    secondaryHex !== shipped.secondaryColor.toLowerCase() ||
    wordmark.trim() !== shipped.productName ||
    logo !== shipped.logoMark;

  return (
    /* The canvas holds this tab to a 620px column rather than the screen's
       880px: the identity card is a form of short rows, and stretching it
       would leave the controls stranded from their labels. */
    <div className="set-brand-column">
      <section className="set-section" data-testid="branding-tab">
        <div className="set-section-head">
          <div>
            <h2>Deployment identity</h2>
            <p>Applies live across every screen.</p>
          </div>
        </div>

        <div className="set-brand-card">
          <div className="set-brand-row">
            <span className="set-brand-label" id="brand-primary-label">Primary</span>
            <span
              className="set-swatch"
              aria-hidden
              style={{ background: primaryHex ?? "transparent" }}
            />
            <input
              className="set-hex"
              value={primary}
              onChange={(event) => setPrimary(event.target.value)}
              spellCheck={false}
              disabled={!canManage}
              aria-invalid={primaryHex === null}
              aria-label="Primary hex value"
              data-testid="brand-primary-hex"
            />
            <label className="set-color-pick" title="Colour picker">
              <Icon name="pencil" size={14} />
              <input
                type="color"
                value={primaryHex ?? shipped.primaryColor}
                onChange={(event) => setPrimary(event.target.value)}
                disabled={!canManage}
                aria-label="Primary colour picker"
              />
            </label>
          </div>

          <div className="set-brand-row">
            <span className="set-brand-label">Secondary</span>
            <span
              className="set-swatch"
              aria-hidden
              style={{ background: secondaryHex ?? "transparent" }}
            />
            <input
              className="set-hex"
              value={secondary}
              onChange={(event) => setSecondary(event.target.value)}
              spellCheck={false}
              disabled={!canManage}
              aria-invalid={secondaryHex === null}
              aria-label="Secondary hex value"
              data-testid="brand-secondary-hex"
            />
            <label className="set-color-pick" title="Colour picker">
              <Icon name="pencil" size={14} />
              <input
                type="color"
                value={secondaryHex ?? shipped.secondaryColor}
                onChange={(event) => setSecondary(event.target.value)}
                disabled={!canManage}
                aria-label="Secondary colour picker"
              />
            </label>
            <button
              type="button"
              className="icon-btn"
              style={{ marginLeft: "auto" }}
              onClick={swap}
              disabled={!canManage}
              title="Swap primary and secondary"
              aria-label="Swap primary and secondary"
            >
              <Icon name="swap" size={14} />
            </button>
          </div>

          <div className="set-brand-row" ref={logoRef}>
            <span className="set-brand-label">Logo</span>
            <span className="set-logo-box">
              <img src={logo} alt="" aria-hidden />
            </span>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setLogoOpen((open) => !open)}
              disabled={!canManage}
              aria-haspopup="menu"
              aria-expanded={logoOpen}
              data-testid="brand-change-logo"
            >
              Change logo
            </button>
            {logoOpen && (
              <div className="set-logo-pop" role="menu">
                <label className="set-logo-upload">
                  <Icon name="upload" size={14} />
                  Upload a file
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(event) => onLogoFile(event.target.files?.[0])}
                    aria-label="Upload logo file"
                  />
                </label>
                <input
                  className="set-logo-url"
                  defaultValue={logo.startsWith("data:") ? "" : logo}
                  placeholder="Or paste a URL"
                  spellCheck={false}
                  aria-label="Logo URL or path"
                  onChange={(event) => setLogo(event.target.value)}
                />
              </div>
            )}
          </div>

          <div className="set-brand-row">
            <span className="set-brand-label">From the logo</span>
            {logoColors && logoColors.length > 0 ? (
              <>
                <span className="set-logo-colors">
                  {logoColors.map((color) => (
                    <span
                      key={color}
                      className="set-swatch"
                      title={color}
                      aria-hidden
                      style={{ background: color }}
                    />
                  ))}
                </span>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{ marginLeft: "auto" }}
                  disabled={!canManage}
                  onClick={() => {
                    setPrimary(logoColors[0] ?? primary);
                    setSecondary(logoColors[1] ?? logoColors[0] ?? secondary);
                  }}
                  data-testid="brand-use-logo-colors"
                >
                  Use these
                </button>
              </>
            ) : (
              <span className="field-hint" style={{ flex: 1 }}>{logoNote}</span>
            )}
          </div>

          <div className="set-brand-row">
            <span className="set-brand-label">Wordmark</span>
            <input
              className="input"
              style={{ flex: "1 1 200px", minWidth: 0, maxWidth: 260, height: 32 }}
              value={wordmark}
              onChange={(event) => setWordmark(event.target.value)}
              disabled={!canManage}
              aria-label="Wordmark"
              data-testid="brand-wordmark"
            />
          </div>

          <div className="set-brand-row set-brand-preview-row">
            <span className="set-brand-label">Preview</span>
            <span className="set-brand-sample set-brand-sample-primary">Ask Copilot</span>
            <span className="set-brand-sample set-brand-sample-secondary">Configure</span>
            <span className="set-brand-sample set-brand-sample-pill">{wordmark || shipped.productName}</span>
          </div>
        </div>

        <div className="set-brand-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={reset}
            disabled={!canManage || !differsFromDefault}
            data-testid="brand-reset"
          >
            Reset to default
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canManage || !dirty || !primaryHex || !secondaryHex || saving}
            onClick={() => void save()}
            data-testid="brand-save"
          >
            {saving ? "Saving…" : "Save identity"}
          </button>
        </div>
      </section>
    </div>
  );
}
